import { Form, message, Spin } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  useActivateMetric,
  useCreateMetric,
  usePutActivateMetric,
  useUpdateMetric,
} from '@components/metrics-builder/api/queryHooks';
import { useGetMetricList } from '@src/emp-3.0/api/queryHooks';
import { EMetricTypes, EStatus, IMetric } from '@src/emp-3.0/types';
import { ISaveCallback } from '../../types';
import { EFormMode } from '@src/emp-3.0/types/common';
import MetricForm from 'emp-3.0/components/Metrics/IndividualMetric/MetricBuilder';
import { cloneDeep } from 'lodash';
import { useCreatePermissions } from '@src/hooks/usePermissions';
import BuilderWrapper from 'pages/workflow/components/BuilderWrapper';
import { useWorkflowStore } from '@src/pages/workflow/store';
import isEqual from 'lodash/isEqual';
import useErrorFields from '@src/pages/workflow/hooks/useErrorFields';
import FormValidatorContext from '../../FormValidatorContext';
import { formValidator } from '@src/pages/workflow/utils';
import { INDIVIDUAL_METRIC_CONFIG, RATIO_METRIC_CONFIG } from '@src/emp-3.0/constants/metrics';
import { useMetricBuilderStore } from '@src/emp-3.0/components/Metrics/IndividualMetric/store';
import { useFlowStore } from '@src/pages/workflow/store/flowBuilder';
import { Footer } from '@src/pages/workflow/components/FlowBuilder/components';
import { useBuildMetricFormData } from '@src/emp-3.0/hooks';

const Layout = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  background-color: white;
  flex-direction: column;
`;

const ContentWrapper = styled.div`
  width: 100%;
  overflow-x: hidden;
`;

interface IMetricsBuilderProps {
  onDeleteMetric?: () => void;
  lastPath?: string;
}

export interface IMetricsBuilderRef {
  save: (cb?: ISaveCallback) => Promise<void>;
}

const MetricsBuilder = () => {
  const { metricId = 'new' } = useParams<any>();

  const location = useLocation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const cloneId = searchParams.get('clone_id');
  const hasCreatePermission = useCreatePermissions();
  const { buildMetricFormData } = useBuildMetricFormData();

  const [serverSideError, setServerSideError] = useState<string>('');
  const [keepHistoricalData, setKeepHistoricalData] = useState<boolean>(true);
  const [hasMetadataChanged, setMetadataChanged] = useState<boolean>(false);

  const { data: userDefinedMetrics = [], isFetching: isMetricListLoading } = useGetMetricList();
  const { mutateAsync: create, isLoading: createLoading } = useCreateMetric();
  const { mutateAsync: update, isLoading: updateLoading } = useUpdateMetric();
  const { mutateAsync: activate, isLoading: isActivateLoading } = useActivateMetric();
  const { mutateAsync: putActivate, isLoading: isPutActivateLoading } = usePutActivateMetric();

  const { setFormMode, formMode, hasChanged, setHasChanged } = useWorkflowStore();
  const { setActiveTool, setDeploymentStatus } = useFlowStore();
  const {
    initialEvent,
    completeEvent,
    setActiveSectionKey,
    resetAll,
    setFormInstance,
    numeratorEvent,
    denominatorEvent,
  } = useMetricBuilderStore();

  const { errorFields, setErrorFields, resetErrorField } = useErrorFields();

  const metricFormInitialValues = useMemo(() => buildMetricFormData(), []);
  const isLoading = isMetricListLoading;
  const isCreateNewMetric = metricId === 'new';

  useEffect(() => {
    setActiveTool(null);
  }, []);

  useEffect(() => {
    setFormInstance(form);
  }, [form, setFormInstance]);

  useEffect(() => {
    if (cloneId) {
      setFormMode(EFormMode.CLONE);
    } else if (isCreateNewMetric) {
      resetAll();
      setFormMode(EFormMode.NEW);
    } else {
      setFormMode(EFormMode.EDIT);
    }

    return () => {
      setFormMode(EFormMode.VIEW);
    };
  }, [cloneId, metricId]);

  useEffect(() => {
    if (formMode === EFormMode.CLONE) {
      setHasChanged(true);
    } else {
      setHasChanged(false);
    }
  }, [isLoading]);

  const metricData = useMemo(() => {
    let tempMetricData: IMetric;

    if (isCreateNewMetric && !cloneId) {
      return { ...metricFormInitialValues };
    } else {
      tempMetricData = cloneDeep(
        userDefinedMetrics.find((x) => x.id === Number(metricId) || x.id === Number(cloneId)),
      ) as IMetric;
    }
    if (tempMetricData && cloneId) {
      tempMetricData.name = `${tempMetricData.name}_Copy`; // rename metric name
    }

    return tempMetricData;
  }, [userDefinedMetrics, metricId, cloneId]);

  // reset keep historic data
  // 2025.04.03 flow group discussion:
  // Before deployment, during the editing phase, the system should remember the customer’s last selection.
  // After deployment, the option should automatically return to the default.
  useEffect(() => {
    if (metricData) {
      if (metricData.status === EStatus.UNDEPLOYED && formMode !== EFormMode.CLONE) {
        setKeepHistoricalData(metricData.keep_historical);
      } else {
        setKeepHistoricalData(true);
      }
    }
  }, [metricData]);

  const createMetric = useCallback(
    async (metric) => {
      await create(
        { newMetric: { ...metric } },
        {
          onSuccess: (data) => {
            message.success('Build new metric success!');
            const url = location.pathname.replace(metricId, data?.data.id);
            navigate(url, { replace: true });
          },
          onError: (error: any) => {
            const errorMsg = error?.response?.data?.msg || 'unknown error';
            setServerSideError(errorMsg);
          },
        },
      );
    },
    [create, location.pathname, metricId, navigate],
  );

  const updateMetric = useCallback(
    async (metric) => {
      const { id } = metricData as IMetric;
      await update(
        { id, newMetric: { ...metric } },
        {
          onSuccess: () => {
            message.success('Update metric success!');
          },
          onError: (error: any) => {
            const errorMsg = error?.response?.data?.msg || 'unknown error';
            setServerSideError(errorMsg);
          },
        },
      );
    },
    [metricData, update],
  );

  const validateForm = useCallback(async () => {
    try {
      // reset error
      setErrorFields({});
      setServerSideError('');
      // required check for flow form
      const shouldOpenedMetricFormSections: any[] = [];
      let values: any;
      if (form.getFieldValue(['metrics_type']) === EMetricTypes.RATIO_METRIC) {
        values = await formValidator(form, RATIO_METRIC_CONFIG, false).catch((e) => {
          const errorKeys = Object.keys(e.errorFields) || [];
          const metricError = errorKeys.some((key) =>
            ['numerator', 'denominator'].some((target) => key.includes(target)),
          );
          const configurationError = errorKeys.some((key) =>
            ['is_higher_better', 'unit'].some((target) => key.includes(target)),
          );
          if (metricError) shouldOpenedMetricFormSections.push('2');
          if (configurationError) shouldOpenedMetricFormSections.push('3');

          setErrorFields(e.errorFields);
          return false;
        });
        // isEditing check for event rule builder
        if (numeratorEvent || denominatorEvent) {
          shouldOpenedMetricFormSections.push('2');
          setErrorFields({ event: 'Editing' });
        }
      } else {
        values = await formValidator(form, INDIVIDUAL_METRIC_CONFIG, false).catch((e) => {
          const errorKeys = Object.keys(e.errorFields) || [];
          const eventError = errorKeys.some((key) =>
            ['first_event', 'second_event', 'event'].some((target) => key.includes(target)),
          );
          const configurationError = errorKeys.some((key) =>
            [
              'distribution',
              'within_range',
              'is_higher_better',
              'attribute',
              'unit',
              'without_event',
            ].some((target) => key.includes(target)),
          );

          if (eventError) shouldOpenedMetricFormSections.push('2');
          if (configurationError) shouldOpenedMetricFormSections.push('3');

          setErrorFields(e.errorFields);
          return false;
        });

        // isEditing check for event rule builder
        if (initialEvent || completeEvent) {
          shouldOpenedMetricFormSections.push('2');
          setErrorFields({ event: 'Editing' });
        }
      }
      // first check if there is any error in the form
      if (shouldOpenedMetricFormSections.length > 0) {
        setActiveSectionKey(shouldOpenedMetricFormSections);
        return false;
      }
      // second check if there is any error in the event rule builder
      if (shouldOpenedMetricFormSections.length > 0) {
        setActiveSectionKey(shouldOpenedMetricFormSections);
        return false;
      }

      // for keep historical data
      values['keep_historical'] = keepHistoricalData;

      return values;
    } catch (err) {
      console.log('validate form error', err);
    }
  }, [
    completeEvent,
    form,
    initialEvent,
    keepHistoricalData,
    setActiveSectionKey,
    setErrorFields,
    numeratorEvent,
    denominatorEvent,
  ]);

  const handleSave = useCallback(async () => {
    try {
      const values = await validateForm();
      // for keep historical data
      values['keep_historical'] = keepHistoricalData;

      if (isCreateNewMetric) {
        await createMetric(values);
      } else {
        await updateMetric(values);
      }
    } catch (e) {
      console.log('create/update metric error: ', e);
    }
  }, [createMetric, isCreateNewMetric, keepHistoricalData, updateMetric, validateForm]);

  const handleCancel = () => {
    if (isCreateNewMetric) {
      navigate(`/metric-builder`);
    } else {
      navigate(`/metric-builder/metric/${metricId}`);
    }
  };

  const errorMsg = useMemo(() => {
    if (Object.values(errorFields).some((x) => x === 'Required')) {
      return '* Required fields in red are missing.';
    } else if (Object.values(errorFields).some((x) => x === 'Editing')) {
      return 'Still has unsaved event changes.';
    } else {
      return serverSideError;
    }
  }, [errorFields, serverSideError]);

  const handleDeploy = useCallback(async () => {
    const values = await validateForm();
    if (!values) {
      return;
    }
    setActiveTool('deployment');
    setDeploymentStatus('inprogress');

    if (isCreateNewMetric) {
      await activate(values, {
        onSuccess: (data) => {
          console.log('deploy success');
          const url = location.pathname.replace('new', data?.data.id);
          navigate(url, { replace: true });
          setDeploymentStatus('success');
        },
        onError: (error: any) => {
          setDeploymentStatus('failed');
          const errorMsg = error?.response?.data?.msg || 'unknown error';
          setServerSideError(errorMsg);
        },
      });
    } else {
      await putActivate(
        { metric_id: Number(metricId), payload: values },
        {
          onSuccess: (data) => {
            console.log('deploy success');
            setDeploymentStatus('success');
          },
          onError: (error: any) => {
            setDeploymentStatus('failed');
            const errorMsg = error?.response?.data?.msg || 'unknown error';
            setServerSideError(errorMsg);
          },
        },
      );
    }
  }, [
    activate,
    isCreateNewMetric,
    metricId,
    putActivate,
    setActiveTool,
    setDeploymentStatus,
    validateForm,
  ]);

  const saveLoading = createLoading || updateLoading;
  const deployLoading = isActivateLoading || isPutActivateLoading;
  const footerButtonLoading = saveLoading || deployLoading;
  const actionButtonProps = {
    showConfirm: (!isCreateNewMetric && hasMetadataChanged) || !keepHistoricalData,
    disabled: !hasCreatePermission || footerButtonLoading,
    confirmProps: {
      cancelButtonProps: { disabled: deployLoading || saveLoading },
    },
  };

  return (
    <BuilderWrapper
      error={errorMsg}
      footer={() => {
        return (
          <Footer
            type={'metric'}
            showKeepHistoricalData={!isCreateNewMetric}
            checkboxProps={{
              checked: keepHistoricalData,
              disabled: footerButtonLoading,
              onChange: (e) => {
                setKeepHistoricalData(e.target.checked);
              },
            }}
            cancelButtonProps={{
              showConfirm: hasChanged,
              disabled: footerButtonLoading,
              onClick: handleCancel,
            }}
            saveButtonProps={{
              onClick: handleSave,
              loading: saveLoading,
              ...actionButtonProps,
            }}
            deployButtonProps={{
              onClick: handleDeploy,
              loading: deployLoading,
              ...actionButtonProps,
            }}
          />
        );
      }}
    >
      <Spin spinning={saveLoading || deployLoading || isMetricListLoading} indicator={undefined}>
        <Layout>
          <ContentWrapper>
            <FormValidatorContext.Provider value={{ enableRequired: false, errorFields }}>
              <MetricForm
                metric={metricData}
                formProps={{
                  form: form,
                  onValuesChange: (changedValues, values) => {
                    resetErrorField(changedValues);
                    setServerSideError('');

                    const formValues = buildMetricFormData(values);
                    const metricValues = buildMetricFormData(metricData);

                    const isChange = !isEqual(formValues, metricValues);
                    const metadataChanged = !isEqual(
                      { definition: formValues.definition, metrics_type: formValues.metrics_type },
                      {
                        definition: formValues.definition,
                        metrics_type: formValues.metrics_type,
                      },
                    );
                    setHasChanged(isChange);
                    setMetadataChanged(metadataChanged);
                  },
                }}
              />
            </FormValidatorContext.Provider>
          </ContentWrapper>
        </Layout>
      </Spin>
    </BuilderWrapper>
  );
};

export default React.forwardRef<IMetricsBuilderRef, IMetricsBuilderProps>(MetricsBuilder);
